import { Dialog, Transition } from "@headlessui/react"
import type { FormikErrors, FormikTouched } from "formik"
import { Formik } from "formik"
import React, { Fragment, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { HiX } from "react-icons/hi"
import * as Yup from "yup"

import SearchSelectMultiple from "../../../../components/searchSelectMultiple"
import type { Team } from "../../../../constants/types"
import type { DestinationOption } from "../index"
import { USER_ROLES, UserRole } from "../../../../constants/userRoles"
import { twClassNames } from "../../../../helpers"
import { isLevi } from "../../../../helpers/isLevi"

export type InviteForm = {
  email: string
  language: string
  role: UserRole
  destinations: string[]
}

type Props = {
  open: boolean
  selectedTeam?: Team
  onClose: () => void
  destinations: DestinationOption[]
  onSubmit: (values: InviteForm) => Promise<void> | void
}

export const InviteMemberModal: React.FC<Props> = ({ open, onClose, destinations, onSubmit, selectedTeam }) => {
  const { t, i18n } = useTranslation()
  const options = useMemo(() => destinations, [destinations])
  const isLeviTeam = isLevi(selectedTeam?.id) ?? false

  const initialValues: InviteForm = {
    email: "",
    language: i18n.resolvedLanguage ?? "en",
    role: USER_ROLES.ADMIN,
    destinations: []
  }

  const schema = Yup.object({
    email: Yup.string().email(t("common.validation.invalidEmail")).required(t("common.validation.required")),
    language: Yup.string().oneOf(["en", "fi"]).required(),
    role: Yup.mixed<UserRole>().oneOf(Object.values(USER_ROLES)).required(),
    destinations: Yup.array()
      .of(Yup.string())
      .when("role", {
        is: USER_ROLES.ADMIN,
        then: s => s.optional(),
        otherwise: s => s.min(1, t("common.validation.minOne")).required()
      })
  })

  const fieldClass = (name: keyof InviteForm, touched: FormikTouched<InviteForm>, errors: FormikErrors<InviteForm>) =>
    twClassNames("tw-form-control", touched[name] && errors[name] ? "tw-is-invalid" : "")

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="tw-relative tw-z-[9999]" onClose={() => {}}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="tw-transition tw-ease-out tw-duration-200"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-transition tw-ease-in tw-duration-150"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0">
          <div className="tw-fixed tw-inset-0 tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-[9998] tw-bg-black/60" />
        </Transition.Child>

        {/* Centered panel */}
        <div
          className="tw-fixed tw-inset-0 tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-[9999] tw-grid
            tw-place-items-center tw-overflow-y-auto">
          <div className="tw-flex tw-min-h-full tw-w-1/2 tw-items-center tw-justify-center tw-p-4">
            <Transition.Child
              as={Fragment}
              enter="tw-transition tw-ease-out tw-duration-200"
              enterFrom="tw-opacity-0 tw-translate-y-2 tw-scale-95"
              enterTo="tw-opacity-100 tw-translate-y-0 tw-scale-100"
              leave="tw-transition tw-ease-in tw-duration-150"
              leaveFrom="tw-opacity-100 tw-translate-y-0 tw-scale-100"
              leaveTo="tw-opacity-0 tw-translate-y-2 tw-scale-95">
              <Dialog.Panel className="tw-w-full tw-max-w-lg tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-xl">
                <Dialog.Title className="tw-mb-4 tw-text-xl tw-font-semibold tw-text-dark">
                  {t("account.team.inviteMemberModal.title")}
                </Dialog.Title>
                <Formik<InviteForm>
                  initialValues={initialValues}
                  validationSchema={schema}
                  validateOnBlur={false}
                  validateOnChange={false}
                  onSubmit={async (values, { setSubmitting }) => {
                    try {
                      await onSubmit(values)
                      onClose()
                    } finally {
                      setSubmitting(false)
                    }
                  }}>
                  {formik => (
                    <form onSubmit={formik.handleSubmit} className="tw-grid tw-grid-cols-6 tw-gap-4">
                      {/* Email */}
                      <div className="tw-relative tw-col-span-6">
                        <label htmlFor="email" className="tw-form-label">
                          {t("account.team.form.email")}
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.email}
                          className={fieldClass("email", formik.touched, formik.errors)}
                          placeholder="john@example.com"
                        />
                        <div className="tw-invalid-tooltip">{formik.errors.email}</div>
                      </div>

                      {/* Role */}
                      <div className="tw-relative tw-col-span-6">
                        <label htmlFor="role" className="tw-form-label">
                          {t("account.team.form.role")}
                        </label>
                        <select
                          id="role"
                          name="role"
                          value={formik.values.role}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={fieldClass("role", formik.touched, formik.errors)}>
                          <option value={USER_ROLES.ADMIN}>{t("common.roles.admin")}</option>
                          <option value={USER_ROLES.MAINTAINER}>{t("common.roles.maintainer")}</option>
                          {isLeviTeam && (
                            <option value={USER_ROLES.LEVI_MAINTAINER}>{t("common.roles.levi_maintainer")}</option>
                          )}
                        </select>
                        <div className="tw-invalid-tooltip">{formik.errors.role}</div>
                      </div>

                      {/* Destinations, shown only for maintainer */}
                      {formik.values.role !== USER_ROLES.ADMIN && (
                        <div className="tw-relative tw-col-span-6">
                          <label htmlFor="destinations" className="tw-form-label">
                            {t("account.team.form.destinations")}
                          </label>
                          <SearchSelectMultiple
                            id="destinations"
                            closeOnSelect
                            data={options}
                            nameKey="name"
                            valueKey="id"
                            indexKey="id"
                            placeholder={t("account.team.placeholders.destinations")}
                            onChange={(value: string[]) => formik.setFieldValue("destinations", value)}
                            onBlur={formik.handleBlur}
                            value={formik.values.destinations}
                            errors={formik.touched.destinations ? (formik.errors.destinations as string) : undefined}
                            limit={100}
                          />
                          <div className="tw-invalid-tooltip">{formik.errors.destinations}</div>

                          {/* Selected list */}
                          {formik.values.destinations?.length > 0 && (
                            <div className="tw-mt-4">
                              <h5 className="tw-mb-2 tw-text-xs tw-font-medium tw-text-dark-50">
                                {t("account.team.form.destinationsAccess")}
                              </h5>
                              <ul className="tw-list-disc tw-space-y-2">
                                {formik.values.destinations.map(id => {
                                  const item = options.find(o => o.id === id)
                                  return (
                                    <li
                                      key={id}
                                      className="tw-flex tw-items-center tw-justify-between tw-border-l-4
                                        tw-border-l-primary tw-bg-gray-50 tw-py-1 tw-pl-4">
                                      <span className="tw-flex tw-flex-col">
                                        <span className="tw-text-sm tw-text-dark">{item?.name ?? id}</span>
                                        {item?.accessCode && (
                                          <span className="tw-text-xs tw-text-dark-50">{item.accessCode}</span>
                                        )}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          formik.setFieldValue(
                                            "destinations",
                                            formik.values.destinations.filter(d => d !== id)
                                          )
                                        }
                                        className="tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center
                                          tw-rounded-full hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2
                                          focus:tw-ring-primary/40"
                                        aria-label={t("account.team.button.remove")}
                                        title={t("account.team.button.remove")}>
                                        <HiX className="tw-h-4 tw-w-4 tw-text-dark-50" />
                                      </button>
                                    </li>
                                  )
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="tw-col-span-6 tw-mt-2 tw-flex tw-justify-end tw-gap-3">
                        <button type="button" className="tw-btn tw-btn-outline" onClick={onClose}>
                          {t("account.team.button.cancel")}
                        </button>
                        <button type="submit" className="tw-btn tw-btn-primary" disabled={formik.isSubmitting}>
                          {t("account.team.button.sendInvite")}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
